import React, { useEffect, useState, useRef, useCallback } from 'react';
import { getRandomPublicStories, submitFeedback, reactStory, getStoryPDFDownloadLink } from '../../api/storyApi';
import { updateUserLanguage } from '../../api/userApi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import FeedbackDialog from './FeedbackDialog';
import Modal from '../common/Modal';
import languageOptions from "../../config/languageOptions";
import './PublicStories.css';
import { useTranslation } from 'react-i18next';
import { useSectionVisibility } from '../../hooks/useSectionVisibility';
import LoadingOverlay from '../common/LoadingOverlay';

// const PAGE_SIZE = 6;
const MAX_LOADED_PUBLIC_STORIES = 96;

const PublicStories = ({ onLoaded, onVisible }) => {
    const [stories, setStories] = useState([]);
    const [selectedStory, setSelectedStory] = useState(null);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showLanguageModal, setShowLanguageModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [selectedLanguage, setSelectedLanguage] = useState("");
    const { isAuthenticated, user } = useAuth();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const firstLoad = useRef(true);
    const ref = useSectionVisibility('public-stories', onVisible);
    const [pageIndex, setPageIndex] = useState(0);
    const [pages, setPages] = useState([]);
    
    const fetchNextPage = useCallback(async () => {
            if (loading) return;

            if (!selectedLanguage) return;
            const language = selectedLanguage.split("-")[0];

            const nextIndex = pageIndex + 1;
            if (pages[nextIndex]) {
                setPageIndex(nextIndex);
                return;
            }

            const exclude = pages.flat().map(s => s._id);
            if (exclude.length > MAX_LOADED_PUBLIC_STORIES) {
                setPageIndex(0);
                return;
            }

            setLoading(true);
            try {
                // const excludeJoined = Array.from(exclude).join(',');
                const response = await getRandomPublicStories(language, exclude);
                if (!response.success || response.stories.length === 0) {
                    if (pages.length === 0) {
                        setPages([[]]);
                    }
                    setPageIndex(0);
                } else {
                    setPages(prevPages => {
                        const newPages = [...prevPages, response.stories];
                        setPageIndex(newPages.length - 1);
                        return newPages;
                    });
                }
            } catch (error) {
                setError('publicStories.fetchingFailed');
            } finally {
                setLoading(false);
                if (firstLoad.current) {
                    onLoaded(0); // sectionId for PublicStories = 0
                    firstLoad.current = false;
                }
            }
        }, [loading, pages, onLoaded, pageIndex, selectedLanguage]);

    const fetchPrevPage = () => {
        if (pageIndex === 0) {
            setPageIndex(pages.length - 1);
        } else {
            setPageIndex(pageIndex - 1);
        }
    };

    useEffect(() => {
        if (pages.length === 0) {
            fetchNextPage();
        }
    }, [fetchNextPage, pages.length]);

    useEffect(() => {
        setPages([]);
        setPageIndex(0);
    }, [isAuthenticated, selectedLanguage])

    useEffect(() => {
        if (user && user.preferredLanguage) {
            setSelectedLanguage(user.preferredLanguage);
        }
    }, [user]);

    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => setMessage(""), 10000); // 10 seconds
            return () => clearTimeout(timer);
        }
    }, [message]);

    useEffect(() => {
        if (i18n.language) {
            const language = languageOptions.find((item) => item.value.startsWith(i18n.language));
            setSelectedLanguage(language.value);
        }
    }, [i18n.language]);

    const handleLanguageChange = async (language) => {
        if (isAuthenticated) {
            await updateUserLanguage(language);
        }
        i18n.changeLanguage(language.split("-")[0]);
        setSelectedLanguage(language);
    };

    const handlePlayStory = (storySlug) => {
        navigate(`/story/${storySlug}`);
    };

    const openFeedbackDialog = (story) => {
        if (!isAuthenticated) {
            setShowAuthModal(true);
            return;
        }
        setSelectedStory(story);
    };

    const closeFeedbackDialog = () => {
        setSelectedStory(null);
    };

    const handleLike = async (storyId) => {
        setError(null);
        if (!isAuthenticated) {
            setShowAuthModal(true);
            return;
        }
        try {
            await reactStory(storyId, 'like');
            updateStoryLikes(storyId, 'like');    
        } catch (error) {
            setError('publicStories.likeError');
        }
    };

    const handleDislike = async (storyId) => {
        setError(null);
        if (!isAuthenticated) {
            setShowAuthModal(true);
            return;
        }
        try {
            await reactStory(storyId, 'dislike');
            updateStoryLikes(storyId, 'dislike');    
        } catch (error) {
            setError('publicStories.likeError');
        }
    };

    const handleFeedbackSubmit = async (storyId, feedback) => {
        setError(null);
        if (!isAuthenticated) {
            setShowAuthModal(true);
            return;
        }
        
        try {
            await submitFeedback(storyId, feedback);
            setStories(stories.map(story => (story._id === storyId ? { ...story, userFeedbackText: feedback} : story)));
            setMessage(t('publicStories.feedbackSent'));
        } catch (error) {
            setError('publicStories.feedbackError');
        }
    };

    const updateStoryLikes = (storyId, userReaction) => {
        setStories(stories.map(story => {
            if (story._id === storyId) {
                const newLikes = userReaction === 'like' ? (story.userReaction === 'like' ? story.likes : story.likes + 1) : (story.userReaction === 'like' ? story.likes - 1 : story.likes);
                const newDislikes = userReaction === 'dislike' ? (story.userReaction === 'dislike' ? story.dislikes : story.dislikes + 1) : (story.userReaction === 'dislike' ? story.dislikes - 1 : story.dislikes);
                return { ...story, likes: newLikes, dislikes: newDislikes, userReaction};
            } else return story;
        }
        ));
    };

    const handleDownload = async (storyId) => {
        setError(null);
        setLoading(true);
        try {
            const pdfUrlRequest = await getStoryPDFDownloadLink(storyId);
            const url = pdfUrlRequest.signedUrl;

            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = '';
            anchor.target = '_blank';
            anchor.style.display = 'none';
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
        } catch (error) {
            if (error.response?.status === 404 && error.response?.data?.message === 'PDF not found') {
                setError('publicStories.PDFNotFound')
            } else {
                console.log(error);
                setError('publicStories.downloadError');
            }
        }
        setLoading(false);
    }

    const currentStories = pages[pageIndex] || [];
    return (
        <section id="public-stories" ref={ref} className="story-section" data-bg="url('/images/bg_public_stories.png')" data-filter="brightness(0.5)">
            <h2 className='story-section-h2'>🔍 {t('browseStories.discoverNewStories')} 🔎</h2>
            <div className="story-section-component">

                <button className="language-button" onClick={() => setShowLanguageModal(true)}>
                    <i className="fa-solid fa-language"></i>
                </button>

                {error && <p className="form-error">{t(error)}</p>}
                {message && <p className="message">{message}</p>}

                {/* Loading State */}
                {loading && <LoadingOverlay message={t('publicStories.loading')} />}

                {/* Display Message if No Stories */}
                {!loading && currentStories.length === 0 && (
                    <div className="no-stories-message">
                        <p>{t('publicStories.noStories')}</p>
                    </div>
                )}

                {/* Public Stories Grid */}
                {!loading && currentStories.length > 0 && (
                    <div style={{ display: 'flex' }}>
                        <div className="pagination-controls">
                            <button className='nav-button' onClick={fetchPrevPage} /* disabled={pageIndex === 0} */><i className="fa-solid fa-arrow-left nav-button-icon"></i></button>
                        </div>
                        <div className="story-grid">
                            {currentStories.map((story) => (
                                <div
                                    className="story-card"
                                    key={story._id}
                                >
                                    <div className="story-image-container">
                                        <img
                                            src={story.coverImage}
                                            alt={story.title}
                                            className="story-image"
                                            onClick={() => handlePlayStory(story.slug)}
                                        />
                                        <div className="story-buttons-overlay">
                                            <button
                                                className={`transparent-button like-btn ${story.userReaction === 'like' ? 'active' : ''}`}
                                                onClick={() => handleLike(story._id)}>
                                                👍 {story.likes || 0}
                                            </button>
                                            <button
                                                className={`transparent-button dislike-btn ${story.userReaction === 'dislike' ? 'active' : ''}`}
                                                onClick={() => handleDislike(story._id)}>
                                                👎 {story.dislikes || 0}
                                            </button>
                                            <button
                                                className={`transparent-button feedback-btn ${story.userFeedbackText ? 'active' : ''}`}
                                                onClick={() => openFeedbackDialog(story)}>
                                                💬
                                            </button>
                                            {user && user.subscriptionLevel !== 'free' && (
                                                <button
                                                    className={`transparent-button download-btn `}
                                                    onClick={() => handleDownload(story._id)}>
                                                    ⏬
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="story-title">{story.title}</div>
                                </div>
                            ))}
                        </div>
                        <div className="pagination-controls">
                            <button className='nav-button' onClick={fetchNextPage}><i className="fa-solid fa-arrow-right nav-button-icon"></i></button>
                        </div>
                    </div>
                )}

                <Modal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)}>
                    <h2>{t('publicStories.signInRequired')}</h2>
                    <p>{t('publicStories.signInRequiredText')}</p>
                    <div className="modal-buttons">
                        <button className="action-btn" onClick={() => navigate('/login')}>{t('publicStories.login')}</button>
                        <button className="close-btn" onClick={() => setShowAuthModal(false)}>{t('publicStories.cancel')}</button>
                    </div>
                </Modal>
                {selectedStory && (
                    <FeedbackDialog
                        story={selectedStory}
                        isOpen={!!selectedStory}
                        onClose={closeFeedbackDialog}
                        onSubmit={handleFeedbackSubmit}
                    />
                )}
                <Modal isOpen={showLanguageModal} onClose={() => setShowLanguageModal(false)}>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleLanguageChange(e.target.preferredLanguage.value);
                            setShowLanguageModal(false);
                            }
                        }
                    >
                        <h2>{t('publicStories.selectLanguage')}</h2>
                        <select className="language-dropdown" name="preferredLanguage" defaultValue={selectedLanguage}>
                            {languageOptions.map((lang) => (
                                <option key={lang.value} value={lang.value}>
                                    {lang.label}
                                </option>
                            ))}
                        </select>
                        <button className="big-button action-button" type="submit">
                            <i className="fa-solid fa-check"></i>
                            OK
                        </button>
                    </form>
                </Modal>

            </div>
        </section>

    );
};

export default PublicStories;
